import React, { useState } from 'react';
import { Building, Plus, Edit2, Trash2, Save, X, Laptop, Video, BarChart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

const RoomsSection = ({ 
  formData, 
  handleInputChange, 
  handleArrayChange,
  errors 
}) => {
  // Состояние формы для добавления/редактирования кабинета
  const [roomForm, setRoomForm] = useState({
    id: null,
    name: '',
    capacity: 30,
    equipment: []
  });
  
  // Флаг, показывающий, находимся ли мы в режиме редактирования
  const [isEditing, setIsEditing] = useState(false);
  
  // Доступное оборудование для кабинетов
  const availableEquipment = [
    { id: 'projector', name: 'Проектор', icon: <Video className="w-4 h-4" /> },
    { id: 'computers', name: 'Компьютеры', icon: <Laptop className="w-4 h-4" /> },
    { id: 'lab_equipment', name: 'Лабораторное оборудование', icon: <BarChart className="w-4 h-4" /> },
    { id: 'smartboard', name: 'Интерактивная доска', icon: <Laptop className="w-4 h-4" /> }
  ];
  
  // Функция сброса формы
  const resetForm = () => {
    setRoomForm({
      id: null,
      name: '',
      capacity: 30,
      equipment: []
    });
    setIsEditing(false);
  };
  
  // Функция для обработки изменений в форме кабинета
  const handleRoomFormChange = (e) => {
    const { name, value } = e.target;
    setRoomForm(prev => ({
      ...prev,
      [name]: name === 'capacity' ? parseInt(value) || 10 : value
    }));
  };
  
  // Функция для обработки выбора оборудования
  const handleEquipmentChange = (equipmentId) => {
    setRoomForm(prev => {
      const equipment = [...prev.equipment];
      
      if (equipment.includes(equipmentId)) {
        // Удаляем оборудование если оно уже выбрано
        return {
          ...prev,
          equipment: equipment.filter(id => id !== equipmentId)
        };
      } else {
        // Добавляем новое оборудование
        return {
          ...prev,
          equipment: [...equipment, equipmentId]
        };
      }
    });
  };
  
  // Функция для добавления/обновления кабинета
  const handleSubmitRoom = (e) => {
    e.preventDefault();
    
    // Проверка заполнения названия
    if (!roomForm.name.trim()) {
      alert('Пожалуйста, введите номер/название кабинета');
      return;
    }
    
    // Проверка вместимости
    if (roomForm.capacity < 5 || roomForm.capacity > 300) {
      alert('Вместимость должна быть от 5 до 300 человек');
      return;
    }
    
    if (isEditing) {
      // Обновление существующего кабинета
      handleArrayChange('rooms', 'update', roomForm);
    } else {
      // Добавление нового кабинета
      handleArrayChange('rooms', 'add', roomForm);
    }
    
    // Сброс формы
    resetForm();
  };
  
  // Функция для начала редактирования кабинета
  const startEditingRoom = (room) => {
    setRoomForm({
      id: room.id,
      name: room.name,
      capacity: room.capacity,
      equipment: [...room.equipment]
    });
    setIsEditing(true);
  };
  
  // Функция для удаления кабинета
  const deleteRoom = (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот кабинет?')) {
      handleArrayChange('rooms', 'remove', { id });
    }
  };
  
  // Функция для получения названия оборудования по ID
  const getEquipmentName = (equipmentId) => {
    const equipment = availableEquipment.find(e => e.id === equipmentId);
    return equipment ? equipment.name : equipmentId;
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white flex items-center">
        <Building className="w-5 h-5 mr-2 text-indigo-500" />
        Кабинеты
      </h3>
      
      {/* Форма добавления/редактирования кабинета */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">
          {isEditing ? 'Редактирование кабинета' : 'Добавление нового кабинета'}
        </h4>
        
        <form onSubmit={handleSubmitRoom} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="roomName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Номер/название кабинета
              </label>
              <input
                type="text"
                id="roomName"
                name="name"
                value={roomForm.name}
                onChange={handleRoomFormChange}
                className="form-input w-full"
                placeholder="Например: 101, Лаборатория"
                required
              />
            </div>
            
            <div>
              <label htmlFor="roomCapacity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Вместимость (человек)
              </label>
              <input
                type="number"
                id="roomCapacity"
                name="capacity"
                min="5"
                max="300"
                value={roomForm.capacity}
                onChange={handleRoomFormChange}
                className="form-input w-full"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Оборудование
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {availableEquipment.map(equipment => (
                <label key={equipment.id} className="inline-flex items-center p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="checkbox"
                    checked={roomForm.equipment.includes(equipment.id)}
                    onChange={() => handleEquipmentChange(equipment.id)}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                    {equipment.icon}
                    <span className="ml-1">{equipment.name}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              primary
              size="sm"
              icon={isEditing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            >
              {isEditing ? 'Сохранить' : 'Добавить кабинет'}
            </Button>
            
            {isEditing && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<X className="w-4 h-4" />}
                onClick={resetForm}
              >
                Отменить
              </Button>
            )}
          </div>
        </form>
      </div>
      
      {/* Список кабинетов */}
      <div>
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">Список кабинетов</h4>
        
        {formData.rooms.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">Кабинеты не добавлены</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => document.getElementById('roomName').focus()}
            >
              Добавить первый кабинет
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {formData.rooms.map((room) => (
              <div 
                key={room.id} 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div className="flex-grow">
                    <div className="font-medium text-gray-800 dark:text-gray-200">
                      {room.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Вместимость: {room.capacity} чел.
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => startEditingRoom(room)}
                      className="text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                      title="Редактировать"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRoom(room.id)}
                      className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {room.equipment && room.equipment.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {room.equipment.map(equipmentId => (
                      <span 
                        key={equipmentId}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {getEquipmentName(equipmentId)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <p className="text-xs text-blue-600 dark:text-blue-400">
          Добавьте все доступные кабинеты с их вместимостью и оборудованием. Это позволит системе 
          назначать занятия в подходящих кабинетах, учитывая количество учащихся в группе и требования 
          к оборудованию для различных предметов.
        </p>
      </div>
    </div>
  );
};

export default RoomsSection;